import React from 'react';
import logoutManager from '../logoutManager';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, LogOut, ShieldCheck } from 'lucide-react'; // Using Lucide for cleaner icons

const COEHeader = ({ orgInfo = {}, tenant = {} }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logoutManager.logout({ redirectTo: '/' });
  };

  const handleHome = () => {
    navigate('/coe');
  };

  // Logic to hide Home button on the dashboard
  const isDashboard = location.pathname === '/coe' || location.pathname === '/coe/';

  return (
    <header className="sticky top-0 z-[100] w-full bg-white/80 backdrop-blur-md border-b border-slate-200/60 shadow-sm">
      {/* Top Accent - Indigo/Blue gradient for a premium feel */}
      <div className="h-1 w-full bg-gradient-to-r from-indigo-600 via-blue-600 to-indigo-600" />

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20 lg:h-24">
          
          {/* Brand Section */}
          <div className="flex items-center gap-3 md:gap-5 flex-1 min-w-0">
            {/* Logo Container */}

            <div className="relative group">
              {(orgInfo.logoUrl && orgInfo.name && orgInfo.name !== 'Institution Name') ? (
                <div className="h-10 w-10 md:h-14 md:w-14 rounded-xl border border-slate-200 p-1 bg-white shadow-sm group-hover:shadow-md transition-shadow">
                  <img 
                    src={orgInfo.logoUrl} 
                    alt={orgInfo.name}
                    className="h-full w-full object-contain rounded-lg"
                  />
                </div>
              ) : (
                <div className="h-10 w-10 md:h-14 md:w-14 rounded-xl bg-slate-900 flex items-center justify-center text-white shadow-lg">
                  <ShieldCheck size={20} className="md:w-7 md:h-7" />
                </div>
              )}
            </div>

            {/* Title & Tenant Info */}
            <div className="flex flex-col min-w-0">
              <h1 className="text-sm md:text-xl font-extrabold text-slate-900 truncate tracking-tight leading-none">
                {(orgInfo.name && orgInfo.name !== 'Institution Name') ? orgInfo.name : 'Generic Institution'}
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[9px] md:text-[10px] font-bold bg-indigo-50 text-indigo-700 uppercase tracking-wider border border-indigo-100">
                  COE Portal
                </span>
                {tenant?.name && (
                  <span className="hidden sm:inline-flex text-[11px] text-slate-400 font-medium truncate italic">
                    — {tenant.name}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Action Cluster */}
          <div className="flex items-center gap-2 md:gap-3 ml-4">
            {/* Home Button */}
            {!isDashboard && (
              <button
                onClick={handleHome}
                className="group flex items-center gap-2 px-3 py-2 md:px-5 md:py-2.5 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 rounded-xl transition-all duration-200 shadow-sm active:scale-95"
                title="Return to Dashboard"
              >
                <Home size={18} className="text-indigo-600 group-hover:scale-110 transition-transform" />
                <span className="hidden md:inline text-sm font-bold tracking-wide">Dashboard</span>
              </button>
            )}

            {/* Vertical Divider */}
            <div className="h-8 w-px bg-slate-200/60 mx-1 hidden sm:block" />

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="group flex items-center justify-center gap-2 px-3 py-2 md:px-5 md:py-2.5 bg-slate-900 hover:bg-red-600 text-white border border-transparent rounded-xl transition-all duration-200 shadow-md shadow-slate-200 active:scale-95"
            >
              <span className="hidden md:inline text-sm font-bold tracking-wide">
                Logout
              </span>
              <LogOut size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile-only Tenant Badge - Kept as a subtle footer for the header */}
      {tenant?.name && (
        <div className="sm:hidden border-t border-slate-100 bg-slate-50/50 px-4 py-1.5">
          <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest truncate opacity-70">
            {tenant.name}
          </p>
        </div>
      )}
    </header>
  );
};

export default COEHeader;